import React, {useState, useRef, useEffect, useCallback} from "react";
import {Box, List, ListSubheader, ListItemButton, Typography, Collapse, Link} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MiniBars from "./MiniBars";
import {SeverityDot} from "./cells";
import LaunchesPanel from "./LaunchesPanel";
import {Row, GroupCard, MovedCard, MovedEventCard} from "./TagListMobile";
import {PHONE_STACK as PHONE_TOP} from "./HeaderBar";
import {feedDigest, silentLine, FEED_HELP} from "./feed";
import {fmtMoneyShort, fmtSignedMoney, fmtDay, fmtCompact, fmtPct0, shortProvider, STATUS_META, isNilValue, EMPTY} from "./format";
import {TYPE, TABULAR, NEUTRAL, MONO, WARNING_TEXT, PHONE_BAR} from "./tokens";

const SUB_H = 28;
const SORT = {key: "rev_last", dir: "desc"};   // big number = yesterday's $, trailing = Δ yday (the loss, not the range revenue)

const Sub = ({children, color}) => (
    <ListSubheader disableSticky={false} sx={{
        top: PHONE_TOP, height: SUB_H, lineHeight: `${SUB_H}px`, px: 1.5, fontSize: 11, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.04em", color: color || "text.secondary", bgcolor: NEUTRAL.headerBg, borderBottom: `1px solid ${NEUTRAL.hairline}`,
        display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 2, ...TABULAR,
    }}>
        {children}
    </ListSubheader>
);

/** Partner silent past its landing time: amber card, `usually $X/d → ?`. */
const SilentCard = ({item, onTap}) => (
    <ListItemButton divider onClick={onTap} aria-label={`${shortProvider(item.provider)} not reported, filter the list`}
                    sx={{display: "block", minHeight: 64, py: 1, pl: 1.25, pr: 1.5, borderLeft: "4px solid", borderLeftColor: (t) => PHONE_BAR.warning(t)}}>
        <Box sx={{display: "flex", alignItems: "baseline", gap: 1, minWidth: 0}}>
            <Typography component="span" noWrap sx={{fontSize: 15, fontWeight: 700, flex: "1 1 auto", minWidth: 0}}>{shortProvider(item.provider)}</Typography>
            <Typography component="span" sx={{fontSize: 15, fontWeight: 700, ...TABULAR, flex: "0 0 auto"}}>{fmtMoneyShort(item.usual)}/d → ?</Typography>
        </Box>
        <Typography component="div" noWrap sx={{fontSize: 12, mt: 0.5, color: "text.secondary"}}>
            <Box component="span" sx={{fontWeight: 700, color: WARNING_TEXT}}>NOT REPORTED</Box> · {item.tags} tag{item.tags === 1 ? "" : "s"} · usually lands {item.expected || EMPTY} UTC · last {fmtDay(item.lastDay)} · <Box component="span" sx={{color: "primary.main", fontWeight: 600}}>tap to re-sync</Box>
        </Typography>
    </ListItemButton>
);

/** Launch losing coverage (not yet in alarm). */
const LaunchCard = ({item, onSelect, guard, selected}) => {
    const l = item.launch;
    const first = l.daily && l.daily[0];
    const reported = (l.daily || []).filter((d) => d.reported !== false);
    const last = reported.length ? reported[reported.length - 1] : first;
    const cov = (d) => (d && !isNilValue(d.coverage) ? fmtPct0(d.coverage) : EMPTY);
    return (
        <ListItemButton divider selected={selected} onClick={() => { if (guard()) onSelect(l.tag); }} aria-label={`${l.tag} launch off pace, open details`}
                        sx={{display: "block", minHeight: 80, py: 1, pl: 1.25, pr: 1.5, borderLeft: "4px solid", borderLeftColor: (t) => PHONE_BAR.warning(t), "&.Mui-selected": {bgcolor: NEUTRAL.selected}}}>
            <Box sx={{display: "flex", alignItems: "baseline", gap: 1, minWidth: 0}}>
                <Typography component="span" sx={{fontFamily: MONO, fontSize: 15, fontWeight: 700, width: 44, flex: "0 0 auto"}}>{l.tag}</Typography>
                <Typography component="span" noWrap sx={{fontSize: 13, color: "text.secondary", flex: "1 1 auto", minWidth: 0}}>{l.site || EMPTY}</Typography>
                <Typography component="span" sx={{fontSize: 15, fontWeight: 700, ...TABULAR, flex: "0 0 auto"}}>{fmtMoneyShort(l.last_rev)}</Typography>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 0.5, minWidth: 0}}>
                <MiniBars daily={l.daily || []} metric="coverage" width={61} height={12} gap={2} partialLast={l.pending} label={`${l.tag}: daily coverage`}/>
                <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", flex: "1 1 auto", minWidth: 0, ...TABULAR}}>
                    <Box component="span" sx={{color: "text.primary"}}>{shortProvider(l.provider)}</Box> · day 1 {fmtMoneyShort(l.day1_rev)} → {fmtMoneyShort(l.last_rev)}
                </Typography>
            </Box>
            <Typography component="div" noWrap sx={{fontSize: 12, mt: 0.5, color: "text.secondary"}}>
                <Box component="span" sx={{fontWeight: 700, color: WARNING_TEXT}}>LAUNCH · day {l.day_n}</Box> · coverage {cov(first)} → {cov(last)}{l.pending ? " · ◌ last day not yet reported" : ""}
            </Typography>
        </ListItemButton>
    );
};

/** One folded line that expands to cards. */
const FoldCard = ({label, detail, open, onToggle, tone}) => (
    <ListItemButton divider onClick={onToggle} aria-expanded={open} aria-label={`${label}, ${open ? "collapse" : "expand"}`}
                    sx={{minHeight: 44, py: 0.75, pl: 1.25, pr: 1.5, borderLeft: "4px solid", borderLeftColor: (t) => (tone === "alarm" ? PHONE_BAR.warning(t) : PHONE_BAR.idle(t)), gap: 1}}>
        {tone === "alarm" && <SeverityDot status="WATCH" size={7} sx={{opacity: 0.6}}/>}
        <Typography component="span" sx={{fontSize: 13, fontWeight: 600, ...TABULAR, flex: "0 0 auto"}}>{label}</Typography>
        <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", ...TABULAR, flex: "1 1 auto", minWidth: 0}}>{detail}</Typography>
        <ExpandMoreIcon sx={{fontSize: 18, color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flex: "0 0 auto"}}/>
    </ListItemButton>
);

/**
 * Phone triage (v3 §2.8): the "What happened" feed as cards — new alarms, silent partners, launches off pace, filtering,
 * losses; gains / ongoing / recovered folded — then the launches and the silent footer.
 */
const PhoneTriage = ({feed, launches, rangeDays, compareBase, asOf, overview, onSelect, selectedTag, counts, statuses, onToggleStatus, onFilterProvider, onResync, alarmTags, earnedTags, pct}) => {
    const lastScroll = useRef(0);
    const [open, setOpen] = useState({});
    const [help, setHelp] = useState(false);
    useEffect(() => {
        const h = () => { lastScroll.current = Date.now(); };
        window.addEventListener("scroll", h, {capture: true, passive: true});
        return () => window.removeEventListener("scroll", h, {capture: true});
    }, []);
    const guard = useCallback(() => Date.now() - lastScroll.current > 150, []);
    const toggle = (k) => setOpen((o) => ({...o, [k]: !o[k]}));
    const digest = feedDigest(feed.digest, overview && overview.residual_usd, overview && overview.n_quiet);
    const silent = silentLine(counts || {}, overview && overview.wasted_sent_d1);

    const card = (it) => {
        switch (it.kind) {
            case "alarm": return <Row key={it.key} r={it.row} sort={SORT} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === it.row.tag} compareBase={compareBase}/>;
            case "alarmGroup": return <GroupCard key={it.key} g={it} sort={SORT} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selectedTag={selectedTag} compareBase={compareBase}/>;
            case "silent": return <SilentCard key={it.key} item={it} onTap={() => (onResync ? onResync(it.provider, asOf) : onFilterProvider(it.provider))}/>;
            case "launch": return <LaunchCard key={it.key} item={it} onSelect={onSelect} guard={guard} selected={selectedTag === it.tag}/>;
            case "event": return <MovedEventCard key={it.key} item={it} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selectedTag={selectedTag} compareBase={compareBase} asOf={asOf}/>;
            case "row": return <MovedCard key={it.key} item={it} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === it.tag} compareBase={compareBase} asOf={asOf}/>;
            case "positives": return (
                <React.Fragment key={it.key}>
                    <FoldCard label={`+${it.n} positive`} detail={`${fmtSignedMoney(it.usd)} · ${it.items.slice(0, 3).map((m) => m.tag).join(" ")}${it.n > 3 ? ` +${it.n - 3}` : ""}`} open={Boolean(open[it.key])} onToggle={() => toggle(it.key)}/>
                    <Collapse in={Boolean(open[it.key])} unmountOnExit>
                        {it.items.map((m) => <MovedCard key={m.key} item={m} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === m.tag} compareBase={compareBase} asOf={asOf} child/>)}
                    </Collapse>
                </React.Fragment>
            );
            case "ongoing": return (
                <React.Fragment key={it.key}>
                    <FoldCard tone="alarm" label={`${it.n} ongoing`} detail={`${it.atRisk > 0 ? `${fmtMoneyShort(it.atRisk)}/d at risk · ` : ""}${it.items.slice(0, 3).map((a) => (a.kind === "group" ? `${a.rows.length} ${shortProvider(a.provider)}` : a.row.tag)).join(" · ")}`} open={Boolean(open[it.key])} onToggle={() => toggle(it.key)}/>
                    <Collapse in={Boolean(open[it.key])} unmountOnExit>
                        {it.items.map((a) => (a.kind === "group"
                            ? <GroupCard key={a.key} g={a} sort={SORT} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selectedTag={selectedTag} compareBase={compareBase}/>
                            : <Row key={a.key} r={a.row} sort={SORT} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === a.row.tag} compareBase={compareBase}/>))}
                    </Collapse>
                </React.Fragment>
            );
            case "recovered": return (
                <React.Fragment key={it.key}>
                    <FoldCard label={`${it.n} recovered`} detail={it.rows.map((r) => r.tag).join(" ")} open={Boolean(open[it.key])} onToggle={() => toggle(it.key)}/>
                    <Collapse in={Boolean(open[it.key])} unmountOnExit>
                        {it.rows.map((r) => <Row key={r.tag} r={r} sort={SORT} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === r.tag} compareBase={compareBase}/>)}
                    </Collapse>
                </React.Fragment>
            );
            default: return null;
        }
    };

    return (
        <Box>
            <List disablePadding sx={{"& .MuiListSubheader-root": {position: "sticky"}}}>
                <li>
                    <ul style={{padding: 0, margin: 0, listStyle: "none"}}>
                        <Sub color={feed.digest.newCount ? WARNING_TEXT : undefined}>
                            <span>What happened{asOf ? ` · ${fmtDay(asOf)}` : ""}</span>
                            <Link component="button" type="button" underline="hover" onClick={() => setHelp((v) => !v)} aria-expanded={help}
                                  sx={{fontSize: 11, fontWeight: 700, textTransform: "none", letterSpacing: 0, verticalAlign: "baseline"}}>{help ? "hide help" : "help"}</Link>
                        </Sub>
                        <Collapse in={help} unmountOnExit>
                            <Box sx={{px: 1.5, py: 1, borderBottom: `1px solid ${NEUTRAL.hairline}`}}>
                                {FEED_HELP.map((l, i) => <Typography key={i} sx={{...TYPE.caption, fontSize: 12, lineHeight: 1.45, color: "text.primary"}}>{l}</Typography>)}
                            </Box>
                        </Collapse>
                        <Typography component="div" sx={{px: 1.5, py: 0.75, fontSize: 12, color: "text.secondary", ...TABULAR, borderBottom: `1px solid ${NEUTRAL.hairline}`, lineHeight: 1.4}}>
                            {feed.quiet ? (
                                <>
                                    <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>Nothing new broke</Box>
                                    {!isNilValue(earnedTags) && <> · {earnedTags} tags earned</>}
                                    {overview && overview.yesterday && !isNilValue(overview.yesterday.rev) && <> {fmtMoneyShort(overview.yesterday.rev)}{!isNilValue(pct) ? ` (p${pct})` : ""}</>}
                                </>
                            ) : digest.map((p, i) => (
                                <React.Fragment key={p.text}>{i > 0 && " · "}<Box component="span" sx={{fontWeight: p.strong ? 600 : 400, color: p.strong ? "text.primary" : "inherit"}}>{p.text}</Box></React.Fragment>
                            ))}
                        </Typography>
                        {feed.items.map(card)}
                    </ul>
                </li>
            </List>
            <LaunchesPanel launches={launches} onSelect={onSelect} selectedTag={selectedTag} phone alarmTags={alarmTags}/>
            {(silent.total > 0 || silent.wasted) && (
                <Typography component="div" sx={{...TYPE.caption, px: 1.5, py: 1, ...TABULAR, lineHeight: 1.5}}>
                    <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>Also</Box>{" · "}
                    {silent.parts.map((p, i) => (
                        <React.Fragment key={p.status}>
                            {i > 0 && " · "}
                            <Link component="button" type="button" underline="hover" onClick={() => onToggleStatus(p.status)} aria-pressed={statuses.includes(p.status)}
                                  title={STATUS_META[p.status].hint} sx={{fontSize: "inherit", fontFamily: "inherit", color: "text.secondary", verticalAlign: "baseline"}}>{p.text}</Link>
                        </React.Fragment>
                    ))}
                    {silent.wasted && <> · {fmtCompact(silent.wasted)} searches wasted yesterday</>}
                </Typography>
            )}
        </Box>
    );
};

export default PhoneTriage;
